const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const UNIT_DISPLAY_NAME_CACHE_PREFIX = 'spence_unit_label_';

export const looksLikeUuid = (value: string): boolean => UUID_PATTERN.test(value.trim());

export const decodeDisplayName = (value: string | null, fallbackValue: string): string => {
  if (!value) {
    return fallbackValue;
  }

  return decodeURIComponent(value)
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};

export const readCachedUnitDisplayName = (unitId: string): string | null => {
  if (typeof window === 'undefined' || !unitId.trim()) {
    return null;
  }

  const cached = window.sessionStorage.getItem(`${UNIT_DISPLAY_NAME_CACHE_PREFIX}${unitId.trim()}`);
  return cached?.trim() ? cached.trim() : null;
};

export const cacheUnitDisplayName = (unitId: string, displayName: string): void => {
  if (typeof window === 'undefined' || !unitId.trim() || !displayName.trim()) {
    return;
  }

  window.sessionStorage.setItem(
    `${UNIT_DISPLAY_NAME_CACHE_PREFIX}${unitId.trim()}`,
    displayName.trim()
  );
};

/** Persists org/unit ids from legacy query params before canonical URL cleanup. */
export const persistUnitDetailContextFromSearch = (
  search: string,
  pathUnitId: string | undefined
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(search);
  const orgId =
    params.get('orgId') ??
    params.get('org_id') ??
    params.get('organizationId') ??
    params.get('organization_id');
  const unitDisplayName = params.get('unit');

  if (orgId?.trim()) {
    window.localStorage.setItem('orgId', orgId.trim());
  }

  if (pathUnitId?.trim()) {
    window.localStorage.setItem('unitId', pathUnitId.trim());
  }

  if (unitDisplayName?.trim() && pathUnitId?.trim()) {
    cacheUnitDisplayName(pathUnitId.trim(), unitDisplayName.trim());
  }
};

export const getUnitBreadcrumbNameFromSearch = (
  search: string,
  pathUnitId: string | undefined
): string => {
  const params = new URLSearchParams(search);
  const unitFromQuery = params.get('unit');
  if (unitFromQuery?.trim()) {
    return decodeDisplayName(unitFromQuery, 'Unit');
  }

  const normalizedPathUnitId = pathUnitId?.trim() ?? '';
  if (normalizedPathUnitId) {
    const cached = readCachedUnitDisplayName(normalizedPathUnitId);
    if (cached) {
      return decodeDisplayName(cached, 'Unit');
    }

    if (!looksLikeUuid(normalizedPathUnitId)) {
      return decodeDisplayName(normalizedPathUnitId, 'Unit');
    }
  }

  return 'Unit';
};

export const shouldFetchUnitDisplayName = (
  search: string,
  pathUnitId: string | undefined
): boolean => {
  const params = new URLSearchParams(search);
  if (params.get('unit')?.trim()) {
    return false;
  }

  const normalizedPathUnitId = pathUnitId?.trim() ?? '';
  if (!normalizedPathUnitId || !looksLikeUuid(normalizedPathUnitId)) {
    return false;
  }

  return !readCachedUnitDisplayName(normalizedPathUnitId);
};
